import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, formatDate } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Shield,
  Download,
  Database,
  Globe,
  Plus,
  Trash2,
  Loader2,
  LogOut,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  FileDown,
  Monitor,
  Smartphone,
  Link2,
  Copy,
  Ban,
  ShieldCheck,
} from "lucide-react";

const EXPORT_COLLECTIONS = [
  { key: "orders", label: "Orders" },
  { key: "customers", label: "Customers" },
  { key: "items", label: "Items" },
  { key: "tasks", label: "Tasks" },
  { key: "users", label: "Users" },
  { key: "categories", label: "Categories" },
];

export default function AdminSecurityPage() {
  const [ipWhitelist, setIpWhitelist] = useState({ enabled: false, ips: [] });
  const [myIp, setMyIp] = useState("");
  const [loginAttempts, setLoginAttempts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newIp, setNewIp] = useState("");
  const [newIpDesc, setNewIpDesc] = useState("");
  const [addingIp, setAddingIp] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [backingUp, setBackingUp] = useState(false);
  const [devices, setDevices] = useState([]);
  const [regLinks, setRegLinks] = useState([]);
  const [maxDevices, setMaxDevices] = useState(2);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [generatingLink, setGeneratingLink] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [wlRes, ipRes, attRes, sessRes, devRes, linksRes, maxDevRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/ip-whitelist`, { withCredentials: true }),
        axios.get(`${API}/admin/my-ip`, { withCredentials: true }),
        axios.get(`${API}/admin/login-attempts?limit=50`, { withCredentials: true }),
        axios.get(`${API}/admin/sessions`, { withCredentials: true }),
        axios.get(`${API}/devices`, { withCredentials: true }),
        axios.get(`${API}/devices/registration-links`, { withCredentials: true }),
        axios.get(`${API}/settings/max-devices`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true }),
      ]);
      setIpWhitelist(wlRes.data);
      setMyIp(ipRes.data.ip_address);
      setLoginAttempts(attRes.data);
      setSessions(sessRes.data);
      setDevices(devRes.data);
      setRegLinks(linksRes.data);
      setMaxDevices(maxDevRes.data.max_devices);
      setUsers(usersRes.data);
    } catch (err) {
      console.error("Failed to fetch security data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleWhitelist = async () => {
    setToggling(true);
    try {
      const res = await axios.post(`${API}/admin/ip-whitelist/toggle`, {}, { withCredentials: true });
      setIpWhitelist((prev) => ({ ...prev, enabled: res.data.enabled }));
      toast.success(res.data.enabled ? "IP whitelist enabled" : "IP whitelist disabled");
    } catch (err) {
      toast.error("Failed to toggle whitelist");
    } finally {
      setToggling(false);
    }
  };

  const handleAddIp = async () => {
    if (!newIp.trim()) {
      toast.error("IP address is required");
      return;
    }
    setAddingIp(true);
    try {
      const res = await axios.post(`${API}/admin/ip-whitelist`, {
        ip_address: newIp.trim(),
        description: newIpDesc.trim(),
      }, { withCredentials: true });
      setIpWhitelist((prev) => ({ ...prev, ips: [...prev.ips, res.data] }));
      setNewIp("");
      setNewIpDesc("");
      toast.success("IP added to whitelist");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add IP");
    } finally {
      setAddingIp(false);
    }
  };

  const handleRemoveIp = async (whitelistId) => {
    try {
      await axios.delete(`${API}/admin/ip-whitelist/${whitelistId}`, { withCredentials: true });
      setIpWhitelist((prev) => ({
        ...prev,
        ips: prev.ips.filter((ip) => ip.whitelist_id !== whitelistId),
      }));
      toast.success("IP removed from whitelist");
    } catch (err) {
      toast.error("Failed to remove IP");
    }
  };

  const handleForceLogout = async (sessionToken) => {
    try {
      await axios.delete(`${API}/admin/sessions/${sessionToken}`, { withCredentials: true });
      setSessions((prev) => prev.filter((s) => s.session_token !== sessionToken));
      toast.success("Session terminated");
    } catch (err) {
      toast.error("Failed to terminate session");
    }
  };

  const handleExport = async (collectionName) => {
    setExporting(collectionName);
    try {
      const res = await axios.get(`${API}/admin/export/${collectionName}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `pots_${collectionName}_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${collectionName} exported successfully`);
    } catch (err) {
      toast.error(`Failed to export ${collectionName}`);
    } finally {
      setExporting(null);
    }
  };

  const handleBackup = async () => {
    setBackingUp(true);
    try {
      const res = await axios.get(`${API}/admin/backup`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `pots_backup_${new Date().toISOString().replace(/[:.]/g, "-")}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Database backup downloaded");
    } catch (err) {
      toast.error("Failed to download backup");
    } finally {
      setBackingUp(false);
    }
  };

  const handleGenerateLink = async () => {
    if (!selectedUserId) {
      toast.error("Please select a user");
      return;
    }
    setGeneratingLink(true);
    setGeneratedLink(null);
    try {
      const res = await axios.post(`${API}/devices/generate-link`, {
        user_id: selectedUserId,
      }, { withCredentials: true });
      const baseUrl = window.location.origin;
      const fullLink = `${baseUrl}/register-device/${res.data.token}`;
      setGeneratedLink({
        link: fullLink,
        user_name: res.data.user_name,
        user_email: res.data.user_email,
        expires_in: res.data.expires_in,
      });
      const linksRes = await axios.get(`${API}/devices/registration-links`, { withCredentials: true });
      setRegLinks(linksRes.data);
      toast.success("Registration link generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate link");
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyLink = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink.link);
      toast.success("Link copied to clipboard!");
    }
  };

  const handleBlockDevice = async (deviceId) => {
    try {
      await axios.put(`${API}/devices/${deviceId}/block`, {}, { withCredentials: true });
      setDevices((prev) => prev.map((d) => d.device_id === deviceId ? { ...d, status: "blocked" } : d));
      toast.success("Device blocked");
    } catch (err) {
      toast.error("Failed to block device");
    }
  };

  const handleUnblockDevice = async (deviceId) => {
    try {
      await axios.put(`${API}/devices/${deviceId}/unblock`, {}, { withCredentials: true });
      setDevices((prev) => prev.map((d) => d.device_id === deviceId ? { ...d, status: "active" } : d));
      toast.success("Device unblocked");
    } catch (err) {
      toast.error("Failed to unblock device");
    }
  };

  const handleRemoveDevice = async (deviceId) => {
    try {
      await axios.delete(`${API}/devices/${deviceId}`, { withCredentials: true });
      setDevices((prev) => prev.filter((d) => d.device_id !== deviceId));
      toast.success("Device removed");
    } catch (err) {
      toast.error("Failed to remove device");
    }
  };

  const handleUpdateMaxDevices = async (val) => {
    try {
      const res = await axios.put(`${API}/settings/max-devices`, { max_devices: val }, { withCredentials: true });
      setMaxDevices(res.data.max_devices);
      toast.success(`Max devices set to ${res.data.max_devices}`);
    } catch (err) {
      toast.error("Failed to update setting");
    }
  };


  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-60" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-security-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl tracking-tight">Security & Backup</h1>
        <p className="text-muted-foreground mt-1">IP security, data export, and database backup</p>
      </div>

      <Tabs defaultValue="ip-security" className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="ip-security" className="rounded-sm">IP Security</TabsTrigger>
          <TabsTrigger value="login-log" className="rounded-sm">Login Log ({loginAttempts.length})</TabsTrigger>
          <TabsTrigger value="sessions" className="rounded-sm">Active Sessions ({sessions.length})</TabsTrigger>
          <TabsTrigger value="export" className="rounded-sm">Export & Backup</TabsTrigger>
          <TabsTrigger value="devices" className="rounded-sm">Devices ({devices.length})</TabsTrigger>
        </TabsList>

        {/* IP Security Tab */}
        <TabsContent value="ip-security" className="space-y-4">
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Shield className="w-5 h-5" /> IP Whitelist
                  </CardTitle>
                  <CardDescription>Only allow login from approved IP addresses</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {ipWhitelist.enabled ? "Enabled" : "Disabled"}
                  </span>
                  <Switch
                    checked={ipWhitelist.enabled}
                    onCheckedChange={handleToggleWhitelist}
                    disabled={toggling}
                    data-testid="ip-whitelist-toggle"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {ipWhitelist.enabled && ipWhitelist.ips.length === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-amber-800">
                    Whitelist is enabled but empty. Add your IP first to avoid being locked out.
                  </p>
                </div>
              )}

              <div className="bg-muted/50 rounded-sm p-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <Globe className="w-4 h-4 text-muted-foreground" />
                  <span>Your current IP: <strong className="font-mono">{myIp}</strong></span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setNewIp(myIp)}
                  data-testid="use-my-ip-btn"
                >
                  Use this IP
                </Button>
              </div>

              <div className="flex gap-2">
                <Input
                  placeholder="IP address (e.g. 203.0.113.50)"
                  value={newIp}
                  onChange={(e) => setNewIp(e.target.value)}
                  className="rounded-sm font-mono flex-1"
                  data-testid="new-ip-input"
                />
                <Input
                  placeholder="Description (optional)"
                  value={newIpDesc}
                  onChange={(e) => setNewIpDesc(e.target.value)}
                  className="rounded-sm flex-1"
                  data-testid="new-ip-desc-input"
                />
                <Button onClick={handleAddIp} disabled={addingIp} data-testid="add-ip-btn">
                  {addingIp ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Add
                </Button>
              </div>

              {ipWhitelist.ips.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-mono text-xs uppercase">IP Address</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Description</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Added By</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Date</TableHead>
                      <TableHead className="font-mono text-xs uppercase text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ipWhitelist.ips.map((entry) => (
                      <TableRow key={entry.whitelist_id}>
                        <TableCell className="font-mono">{entry.ip_address}</TableCell>
                        <TableCell className="text-muted-foreground">{entry.description || "-"}</TableCell>
                        <TableCell>{entry.added_by_name || "-"}</TableCell>
                        <TableCell className="text-muted-foreground">{formatDate(entry.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => handleRemoveIp(entry.whitelist_id)}
                            data-testid={`remove-ip-${entry.whitelist_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Login Log Tab */}
        <TabsContent value="login-log">
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg">Login Attempts</CardTitle>
              <CardDescription>Recent login attempts with IP and device info</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-mono text-xs uppercase">Status</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Email</TableHead>
                      <TableHead className="font-mono text-xs uppercase">IP Address</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Reason</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loginAttempts.map((attempt) => (
                      <TableRow key={attempt.attempt_id} data-testid={`login-attempt-${attempt.attempt_id}`}>
                        <TableCell>
                          {attempt.success ? (
                            <Badge className="bg-emerald-50 text-emerald-700 text-xs">
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Success
                            </Badge>
                          ) : attempt.blocked ? (
                            <Badge className="bg-red-50 text-red-700 text-xs">
                              <XCircle className="w-3 h-3 mr-1" /> Blocked
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-50 text-amber-700 text-xs">
                              <AlertTriangle className="w-3 h-3 mr-1" /> Failed
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{attempt.email}</TableCell>
                        <TableCell className="font-mono text-sm">{attempt.ip_address}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{attempt.reason || "-"}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{formatDate(attempt.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {loginAttempts.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">No login attempts recorded</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Active Sessions Tab */}
        <TabsContent value="sessions">
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg">Active Sessions</CardTitle>
              <CardDescription>Force logout suspicious sessions</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-mono text-xs uppercase">User</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Role</TableHead>
                    <TableHead className="font-mono text-xs uppercase">IP Address</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Started</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Expires</TableHead>
                    <TableHead className="font-mono text-xs uppercase text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((sess) => (
                    <TableRow key={sess.session_token}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{sess.user_name || "Unknown"}</div>
                          <div className="text-xs text-muted-foreground">{sess.user_email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs capitalize">{sess.user_role}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{sess.ip_address || "-"}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(sess.created_at)}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(sess.expires_at)}</TableCell>
                      <TableCell className="text-right">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              data-testid={`force-logout-${sess.session_token}`}
                            >
                              <LogOut className="w-4 h-4 mr-1" /> End
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Terminate Session</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will force logout {sess.user_name || "this user"} immediately.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                onClick={() => handleForceLogout(sess.session_token)}
                              >
                                Terminate
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {sessions.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">No active sessions</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Export & Backup Tab */}
        <TabsContent value="export" className="space-y-4">
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <FileDown className="w-5 h-5" /> Data Export (CSV)
              </CardTitle>
              <CardDescription>Export individual collections as CSV files</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {EXPORT_COLLECTIONS.map((coll) => (
                  <Button
                    key={coll.key}
                    variant="outline"
                    className="justify-start rounded-sm h-auto py-3"
                    onClick={() => handleExport(coll.key)}
                    disabled={exporting === coll.key}
                    data-testid={`export-${coll.key}-btn`}
                  >
                    {exporting === coll.key ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    {coll.label}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Database className="w-5 h-5" /> Database Backup
              </CardTitle>
              <CardDescription>
                Download a complete database backup as JSON. Includes all collections except password hashes.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={handleBackup}
                disabled={backingUp}
                className="rounded-sm"
                data-testid="download-backup-btn"
              >
                {backingUp ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Database className="w-4 h-4 mr-2" />
                )}
                Download Full Backup
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Device Management Tab */}
        <TabsContent value="devices" className="space-y-4">
          {/* Generate Registration Link */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Link2 className="w-5 h-5" /> Generate Device Registration Link
              </CardTitle>
              <CardDescription>
                Create a link for a user to register their device. Links expire in 24 hours.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="text-sm font-medium mb-1 block">Select User</label>
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="w-full h-10 rounded-sm border border-input bg-background px-3 text-sm"
                    data-testid="device-user-select"
                  >
                    <option value="">Choose a user...</option>
                    {users.filter(u => u.role !== "super_admin").map((u) => (
                      <option key={u.user_id} value={u.user_id}>
                        {u.name} ({u.email}) - {u.department || u.role}
                      </option>
                    ))}
                  </select>
                </div>
                <Button
                  onClick={handleGenerateLink}
                  disabled={generatingLink || !selectedUserId}
                  className="rounded-sm"
                  data-testid="generate-device-link-btn"
                >
                  {generatingLink ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Link2 className="w-4 h-4 mr-2" />
                  )}
                  Generate Link
                </Button>
              </div>

              {generatedLink && (
                <div className="p-4 rounded-sm bg-emerald-50 border border-emerald-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-emerald-700">
                      Link for {generatedLink.user_name} ({generatedLink.user_email})
                    </p>
                    <Badge variant="outline" className="text-xs">Expires: {generatedLink.expires_in}</Badge>
                  </div>
                  <div className="flex gap-2 items-center">
                    <code className="flex-1 p-2 bg-white rounded-sm text-xs break-all border">
                      {generatedLink.link}
                    </code>
                    <Button size="sm" variant="outline" onClick={handleCopyLink} className="shrink-0 rounded-sm" data-testid="copy-device-link-btn">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-emerald-600">Share this link with the user. They must open it on the device they want to register.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Max Devices Setting */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Smartphone className="w-5 h-5" /> Device Limit
                  </CardTitle>
                  <CardDescription>Maximum devices per user (Super Admin is unlimited)</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={maxDevices}
                    onChange={(e) => handleUpdateMaxDevices(parseInt(e.target.value))}
                    className="h-10 rounded-sm border border-input bg-background px-3 text-sm font-mono"
                    data-testid="max-devices-select"
                  >
                    {[1, 2, 3, 4, 5].map((n) => (
                      <option key={n} value={n}>{n} device{n > 1 ? "s" : ""}</option>
                    ))}
                  </select>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Registered Devices */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Monitor className="w-5 h-5" /> Registered Devices ({devices.length})
              </CardTitle>
              <CardDescription>All registered devices across users</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {devices.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Monitor className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p>No devices registered yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-mono text-xs uppercase tracking-widest">User</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest">Device Name</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest">Status</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest">Registered</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {devices.map((d) => (
                      <TableRow key={d.device_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{d.user_name}</p>
                            <p className="text-xs text-muted-foreground">{d.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Monitor className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">{d.device_name || "Unknown"}</span>
                            {d.is_master && <Badge className="text-[10px]">Master</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={d.status === "active" ? "default" : "destructive"}
                            className="text-xs"
                          >
                            {d.status === "active" ? "Active" : "Blocked"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {d.registered_at ? new Date(d.registered_at).toLocaleDateString() : "-"}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {d.status === "active" ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs rounded-sm"
                                onClick={() => handleBlockDevice(d.device_id)}
                                data-testid={`block-device-${d.device_id}`}
                              >
                                <Ban className="w-3 h-3 mr-1" /> Block
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs rounded-sm"
                                onClick={() => handleUnblockDevice(d.device_id)}
                                data-testid={`unblock-device-${d.device_id}`}
                              >
                                <ShieldCheck className="w-3 h-3 mr-1" /> Unblock
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs text-red-500 rounded-sm"
                              onClick={() => handleRemoveDevice(d.device_id)}
                              data-testid={`remove-device-${d.device_id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Registration Links History */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Link2 className="w-5 h-5" /> Registration Links
              </CardTitle>
              <CardDescription>History of generated device registration links</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {regLinks.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Link2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p>No registration links generated yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-mono text-xs uppercase tracking-widest">User</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest">Status</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest">Created</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest">Expires</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {regLinks.map((link) => {
                      const expired = new Date(link.expires_at) < new Date();
                      return (
                        <TableRow key={link.link_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{link.user_name}</p>
                              <p className="text-xs text-muted-foreground">{link.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            {link.used ? (
                              <Badge className="text-xs bg-emerald-500">Used</Badge>
                            ) : expired ? (
                              <Badge variant="secondary" className="text-xs">Expired</Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">Pending</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {new Date(link.created_at).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {new Date(link.expires_at).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
